import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { apiRequest } from '../lib/utils';
import { 
  Settings, 
  Save,
  ChevronLeft,
  Mail,
  Phone,
  MapPin,
  Palette,
  Building
} from 'lucide-react';

const AdminSettingsPage = () => {
  const [settings, setSettings] = useState({
    company_name: 'Archon Insurance',
    contact_email: 'monte@archoninsurance.com',
    contact_phone: '',
    address: 'Massachusetts',
    primary_color: '#1a365d',
    accent_color: '#B5A064'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await apiRequest('/settings');
      if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({
        company_name: settings.company_name,
        contact_email: settings.contact_email,
        contact_phone: settings.contact_phone || '',
        address: settings.address,
        primary_color: settings.primary_color,
        accent_color: settings.accent_color
      });
      
      await apiRequest(`/admin/settings?${params}`, { method: 'PUT' });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-settings-page">
        <div className="mb-6">
          <Link to="/admin" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-900">Site Settings</h1>
          <p className="text-slate-600 mt-1">Configure your website settings</p>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" /> General Settings
            </CardTitle>
            <Button onClick={saveSettings} disabled={saving} className="bg-[#1a365d]">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Company Information */}
            <div>
              <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Building className="h-5 w-5 text-slate-400" /> Company Information
              </h3>
              <div className="grid gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    className="w-full border rounded-md p-3"
                    value={settings.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      className="w-full border rounded-md p-3 pl-10"
                      value={settings.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <hr />

            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5 text-slate-400" /> Contact Information
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="email"
                      className="w-full border rounded-md p-3 pl-10"
                      value={settings.contact_email}
                      onChange={(e) => handleChange('contact_email', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="tel"
                      className="w-full border rounded-md p-3 pl-10"
                      value={settings.contact_phone}
                      placeholder="(555) 123-4567"
                      onChange={(e) => handleChange('contact_phone', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <hr />

            {/* Branding Colors */}
            <div>
              <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Palette className="h-5 w-5 text-slate-400" /> Brand Colors
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Primary Color (Navy)</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      className="w-12 h-10 border rounded cursor-pointer"
                      value={settings.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                    />
                    <input
                      type="text"
                      className="flex-1 border rounded-md p-2 font-mono text-sm"
                      value={settings.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Used for headers, buttons, and navigation</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Accent Color (Brass)</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      className="w-12 h-10 border rounded cursor-pointer"
                      value={settings.accent_color}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                    />
                    <input
                      type="text"
                      className="flex-1 border rounded-md p-2 font-mono text-sm"
                      value={settings.accent_color}
                      onChange={(e) => handleChange('accent_color', e.target.value)}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Used for highlights and call-to-action buttons</p>
                </div>
              </div>
              
              {/* Color Preview */}
              <div className="mt-4 p-4 bg-slate-100 rounded-lg">
                <p className="text-sm font-medium mb-2">Preview:</p>
                <div className="flex gap-4">
                  <div 
                    className="w-24 h-12 rounded flex items-center justify-center text-white text-xs font-medium"
                    style={{ backgroundColor: settings.primary_color }}
                  >
                    Primary
                  </div>
                  <div 
                    className="w-24 h-12 rounded flex items-center justify-center text-white text-xs font-medium"
                    style={{ backgroundColor: settings.accent_color }}
                  >
                    Accent
                  </div>
                </div>
              </div>
            </div>

            <hr />

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">How Settings Work</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Changes are saved to the database and persist across sessions</li>
                <li>• Color changes require a page refresh to take effect site-wide</li>
                <li>• Contact information appears in the footer and contact pages</li>
                <li>• Company name is used throughout the website</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default AdminSettingsPage;
